import {
  adminV1OrganizationsPaymentsOfferingsBenefitsList,
  adminV1OrganizationsPaymentsOfferingsBenefitsCreate,
  adminV1OrganizationsPaymentsOfferingsBenefitsDestroy,
  adminV1OrganizationsPaymentsOfferingsBenefitsPreviewChangesCreate,
  adminV1OrganizationsPaymentsBenefitsList,
  adminV1OrganizationsPaymentsBenefitsPartialUpdate,
  adminV1OrganizationsPaymentsSubscriberMembershipsBenefitsList,
  adminV1OrganizationsPaymentsSubscriberMembershipsBenefitsCreate,
  adminV1OrganizationsPaymentsBenefitsDestroy,
} from "@/api/admin-benefits/admin-benefits";
import type {
  MembershipBenefit,
  PaginatedMembershipBenefitList,
  BenefitChangePreviewRequest,
  BenefitChangePreviewResponse,
  PaginatedAdminSubscriberBenefitList,
  AdminV1OrganizationsPaymentsBenefitsListParams,
  PatchedAdminSubscriberBenefit,
  AdminSubscriberBenefit,
  AdminV1OrganizationsPaymentsSubscriberMembershipsBenefitsListParams,
  AdminV1OrganizationsProfilesBenefitsListParams,
  AdminManualBenefitCreate,
  PatchedAdminManualBenefitUpdate,
} from "@/api/model";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import type { NonReadonly } from "@/types";
import { isAxiosError } from "axios";

function getDefaultOrganizationId(): number {
  const { currentOrganizationId } = useCurrentOrganization();
  const orgId = currentOrganizationId.value;
  if (!orgId) {
    throw new Error("No default organization found");
  }
  return orgId;
}

export type ApiError = {
  error: string;
  status?: number;
  code?: string;
  details?: unknown;
};

function toApiError(e: unknown): ApiError {
  if (isAxiosError(e)) {
    return {
      error: e.response?.data?.detail ?? e.message,
      status: e.response?.status,
      code: e.response?.data?.code,
      details: e.response?.data,
    };
  }
  return { error: e instanceof Error ? e.message : String(e) };
}

export const getBenefitTemplates = async (
  offeringId: string
): Promise<PaginatedMembershipBenefitList | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsPaymentsOfferingsBenefitsList(
      organizationId,
      offeringId
    );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const createBenefitTemplate = async (
  offeringId: string,
  benefit: NonReadonly<MembershipBenefit>
): Promise<MembershipBenefit | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsPaymentsOfferingsBenefitsCreate(
      organizationId,
      offeringId,
      benefit
    );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const deleteBenefitTemplate = async (
  offeringId: string,
  benefitId: string
): Promise<void | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsPaymentsOfferingsBenefitsDestroy(
      organizationId,
      offeringId,
      benefitId
    );
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const previewBenefitChanges = async (
  offeringId: string,
  request: BenefitChangePreviewRequest
): Promise<BenefitChangePreviewResponse | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response =
      await adminV1OrganizationsPaymentsOfferingsBenefitsPreviewChangesCreate(
        organizationId,
        offeringId,
        request
      );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const getSubscriberBenefits = async (
  params?: AdminV1OrganizationsPaymentsBenefitsListParams
): Promise<PaginatedAdminSubscriberBenefitList | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsPaymentsBenefitsList(
      organizationId,
      params
    );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const getSubscriberBenefitsForMembership = async (
  membershipId: string,
  params?: AdminV1OrganizationsPaymentsSubscriberMembershipsBenefitsListParams
): Promise<PaginatedAdminSubscriberBenefitList | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response =
      await adminV1OrganizationsPaymentsSubscriberMembershipsBenefitsList(
        organizationId,
        membershipId,
        params
      );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const updateSubscriberBenefit = async (
  benefitId: string,
  data: NonReadonly<PatchedAdminSubscriberBenefit>
): Promise<AdminSubscriberBenefit | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsPaymentsBenefitsPartialUpdate(
      organizationId,
      benefitId,
      data
    );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const createSubscriberBenefit = async (
  membershipId: string,
  benefit: NonReadonly<AdminSubscriberBenefit>
): Promise<AdminSubscriberBenefit | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response =
      await adminV1OrganizationsPaymentsSubscriberMembershipsBenefitsCreate(
        organizationId,
        membershipId,
        benefit
      );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const deleteSubscriberBenefit = async (
  benefitId: string
): Promise<void | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsPaymentsBenefitsDestroy(
      organizationId,
      benefitId
    );
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const createManualBenefit = async (
  profileId: number,
  data: AdminManualBenefitCreate
): Promise<AdminSubscriberBenefit | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const { adminV1OrganizationsProfilesManualBenefitsCreate } = await import(
      "@/api/admin-payments-manual-benefits/admin-payments-manual-benefits"
    );
    const response = await adminV1OrganizationsProfilesManualBenefitsCreate(
      organizationId,
      profileId,
      data
    );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const updateManualBenefit = async (
  profileId: number,
  benefitId: string,
  data: PatchedAdminManualBenefitUpdate
): Promise<AdminSubscriberBenefit | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const { adminV1OrganizationsProfilesManualBenefitsPartialUpdate } =
      await import(
        "@/api/admin-payments-manual-benefits/admin-payments-manual-benefits"
      );
    const response =
      await adminV1OrganizationsProfilesManualBenefitsPartialUpdate(
        organizationId,
        profileId,
        benefitId,
        data
      );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const deleteManualBenefit = async (
  profileId: number,
  benefitId: string
): Promise<void | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const { adminV1OrganizationsProfilesManualBenefitsDestroy } = await import(
      "@/api/admin-payments-manual-benefits/admin-payments-manual-benefits"
    );
    await adminV1OrganizationsProfilesManualBenefitsDestroy(
      organizationId,
      profileId,
      benefitId
    );
  } catch (e: unknown) {
    return toApiError(e);
  }
};

export const getProfileBenefits = async (
  profileId: number,
  params?: AdminV1OrganizationsProfilesBenefitsListParams
): Promise<AdminSubscriberBenefit[] | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const { adminV1OrganizationsProfilesBenefitsList } = await import(
      "@/api/admin-profiles/admin-profiles"
    );
    const response = await adminV1OrganizationsProfilesBenefitsList(
      organizationId,
      profileId,
      params
    );
    return response;
  } catch (e: unknown) {
    return toApiError(e);
  }
};
